import { useEffect, useState } from 'react';
import { Clock, Users, TrendingUp, Calendar } from 'lucide-react';

interface UserAttendance {
  user_id: string;
  user_name: string;
  user_email: string;
  total_hours: number;
  total_days: number;
  avg_hours: number;
  total_breaks: number;
}

export default function RelatorioAtendimento() {
  const [report, setReport] = useState<UserAttendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadReport();
  }, [filter]);

  const loadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      // Buscar todos os attendances
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/attendance/all?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        // Agrupar por usuário
        const grouped = groupByUser(data.data);
        setReport(grouped);
      }
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupByUser = (attendances: any[]) => {
    const userMap: { [key: string]: UserAttendance } = {};

    attendances.forEach((att) => {
      const userId = att.user_id;
      
      if (!userMap[userId]) {
        userMap[userId] = {
          user_id: userId,
          user_name: att.user?.name || 'Sem nome',
          user_email: att.user?.email || '',
          total_hours: 0,
          total_days: 0,
          avg_hours: 0,
          total_breaks: 0
        };
      }

      const hours = parseFloat(att.total_hours?.toString() || '0');
      userMap[userId].total_hours += hours;
      userMap[userId].total_days += 1;
    });

    // Calcular média
    Object.values(userMap).forEach((user) => {
      user.avg_hours = user.total_days > 0 ? user.total_hours / user.total_days : 0;
    });

    return Object.values(userMap);
  };

  const calculateTotals = () => {
    const totalHours = report.reduce((sum, user) => sum + user.total_hours, 0);
    const totalUsers = report.length;
    const avgHoursPerUser = totalUsers > 0 ? totalHours / totalUsers : 0;

    return {
      totalHours: totalHours.toFixed(2),
      totalUsers,
      avgHoursPerUser: avgHoursPerUser.toFixed(2)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando relatório...</p>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Relatório de Atendimento</h1>
        <p className="text-gray-600">Visão geral de todos os colaboradores</p>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Horas</p>
              <p className="text-2xl font-bold text-gray-900">{totals.totalHours}h</p>
            </div>
            <Clock className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Colaboradores</p>
              <p className="text-2xl font-bold text-gray-900">{totals.totalUsers}</p>
            </div>
            <Users className="h-12 w-12 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Média por Colaborador</p>
              <p className="text-2xl font-bold text-gray-900">{totals.avgHoursPerUser}h</p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtrar Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({ start_date: '', end_date: '' })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Colaboradores */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Desempenho por Colaborador</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Colaborador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dias Trabalhados</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de Horas</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Média/Dia</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p>Nenhum registro encontrado</p>
                  </td>
                </tr>
              ) : (
                report.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.user_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.user_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.total_days}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.total_hours.toFixed(2)}h
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.avg_hours.toFixed(2)}h
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
